import type React from 'react'
import { createContext, useContext, useMemo, useState } from 'react'
import { isNil, makeLoggers } from 'utils'

const DebugContext = createContext<
  | ({
      setIsDebugMode: (isDebugMode: boolean) => void
      isDebugMode: boolean
    } & ReturnType<typeof makeLoggers>)
  | null
>(null)

export enum LogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3,
  Disabled = 4
}

/**
 * Provides access to logging functions and state that controls whether debug logs are shown.
 *
 * Right now, although it takes a `logLevelThreshold` prop, it really operates with a binary
 * on/off for logging, depending on whether the threshold is set to Debug. In the future,
 * we may support more granular control over logging levels, especially if we start sending logs
 * to an external service like Sentry.
 */
export const DebugProvider: React.FC<{
  children: React.ReactNode
  logLevelThreshold?: LogLevel
}> = ({ children, logLevelThreshold = LogLevel.Disabled }) => {
  const [isDebugMode, setIsDebugMode] = useState(logLevelThreshold === LogLevel.Debug)

  const value = useMemo(
    () => ({ setIsDebugMode, isDebugMode, ...makeLoggers(isDebugMode) }),
    [isDebugMode]
  )

  return <DebugContext.Provider value={value}>{children}</DebugContext.Provider>
}

/**
 * Get the current debug state (and setter for it), plus logging functions.
 * The loggers follow the api for `console`, but will only log if debug mode is enabled.
 *
 * Loggers available:
 * - `log` follows api of `console.log`, but returns the first argument
 * - `logError` follows api of `console.error`, but returns the first argument
 * - `assert` - follows api of `console.assert` but returns the first argument
 * - `pipedAssert` - follows is a partially applied version of `assert` that can be piped
 *
 * See packages/utils/debug.ts for more details on the loggers.
 */
export const useDebug = () => {
  const context = useContext(DebugContext)

  if (isNil(context)) {
    throw new Error('useDebug must be used within a DebugProvider')
  }

  return context
}
